'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useClubRoute } from '@/lib/contexts/club-route-context'
import { login, sendMagicLink } from './actions'

type AuthMode = 'password' | 'magic-link'
type LoginState = { error: string } | { success: true } | null
type MagicLinkState = { error: string } | { success: boolean; message: string } | null

export default function LoginForm() {
  const [mode, setMode] = useState<AuthMode>('password')
  const { subdomain, urlPrefix } = useClubRoute()
  const router = useRouter()

  const [loginState, setLoginState] = useState<LoginState>(null)
  const [magicLinkState, setMagicLinkState] = useState<MagicLinkState>(null)
  const [isPending, setIsPending] = useState(false)

  const isRedirecting = !!(loginState && 'success' in loginState)

  async function handleLogin(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setIsPending(true)
    setLoginState(null)

    const formData = new FormData(e.currentTarget)
    formData.set('subdomain', subdomain)

    const result = await login(null, formData)

    if (result && 'success' in result) {
      setLoginState(result)
      // Use Next.js router for proper client-side navigation + auth state refresh
      router.push(urlPrefix)
      router.refresh()
    } else {
      setLoginState(result)
      setIsPending(false)
    }
  }

  async function handleMagicLink(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setIsPending(true)
    setMagicLinkState(null)

    const formData = new FormData(e.currentTarget)
    formData.set('subdomain', subdomain)

    const result = await sendMagicLink(null, formData)
    setMagicLinkState(result)
    setIsPending(false)
  }

  const state = mode === 'password' ? loginState : magicLinkState

  return (
    <div className="space-y-6">
      {/* Mode Toggle - Cleaner Pills */}
      <div className="flex gap-2 p-1 rounded-xl bg-[var(--surface-1)]">
        <button
          type="button"
          onClick={() => setMode('password')}
          className={`
            flex-1 py-2.5 rounded-lg text-sm font-medium transition-all
            ${mode === 'password'
              ? 'bg-[var(--accent)] text-[var(--base)] shadow-sm'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            }
          `}
        >
          Password
        </button>
        <button
          type="button"
          onClick={() => setMode('magic-link')}
          className={`
            flex-1 py-2.5 rounded-lg text-sm font-medium transition-all
            ${mode === 'magic-link'
              ? 'bg-[var(--accent)] text-[var(--base)] shadow-sm'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            }
          `}
        >
          Magic Link
        </button>
      </div>

      {/* Password Form */}
      {mode === 'password' && (
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              disabled={isPending}
              className="w-full px-4 py-3.5 rounded-xl bg-[var(--base)] border border-[var(--border)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--accent)] transition-colors"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              minLength={6}
              disabled={isPending}
              className="w-full px-4 py-3.5 rounded-xl bg-[var(--base)] border border-[var(--border)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--accent)] transition-colors"
              placeholder="••••••••"
            />
          </div>

          {state && 'error' in state && state.error && (
            <div className="flex items-center gap-3 p-4 rounded-xl bg-[var(--error)]/10 border border-[var(--error)]/20">
              <svg className="w-5 h-5 text-[var(--error)] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-[var(--error)]">{state.error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isPending || isRedirecting}
            className="w-full py-4 rounded-xl font-semibold bg-[var(--accent)] text-[var(--base)] disabled:opacity-50 flex items-center justify-center gap-2 transition-all"
            style={{ boxShadow: '0 4px 16px var(--accent-muted)' }}
          >
            {isPending || isRedirecting ? (
              <>
                <span className="w-5 h-5 border-2 border-[var(--base)] border-t-transparent rounded-full animate-spin" />
                {isRedirecting ? 'Redirecting...' : 'Signing in...'}
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>
      )}

      {/* Magic Link Form */}
      {mode === 'magic-link' && (
        <form onSubmit={handleMagicLink} className="space-y-4">
          <div className="text-center py-4">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3"
              style={{ background: 'linear-gradient(135deg, var(--accent-muted) 0%, var(--accent-subtle) 100%)' }}
            >
              <svg className="w-7 h-7 text-[var(--accent)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-sm text-[var(--text-muted)]">
              We'll send you a secure link to sign in
            </p>
          </div>

          <div>
            <label htmlFor="magic-email" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
              Email
            </label>
            <input
              id="magic-email"
              name="email"
              type="email"
              autoComplete="email"
              required
              disabled={isPending}
              className="w-full px-4 py-3.5 rounded-xl bg-[var(--base)] border border-[var(--border)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--accent)] transition-colors"
              placeholder="you@example.com"
            />
          </div>

          {magicLinkState && 'success' in magicLinkState && magicLinkState.message && (
            <div className="flex items-center gap-3 p-4 rounded-xl bg-[var(--success)]/10 border border-[var(--success)]/20">
              <svg className="w-5 h-5 text-[var(--success)] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-sm text-[var(--success)]">{magicLinkState.message}</p>
            </div>
          )}

          {state && 'error' in state && state.error && (
            <div className="flex items-center gap-3 p-4 rounded-xl bg-[var(--error)]/10 border border-[var(--error)]/20">
              <svg className="w-5 h-5 text-[var(--error)] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-[var(--error)]">{state.error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isPending}
            className="w-full py-4 rounded-xl font-semibold bg-[var(--accent)] text-[var(--base)] disabled:opacity-50 flex items-center justify-center gap-2 transition-all"
            style={{ boxShadow: '0 4px 16px var(--accent-muted)' }}
          >
            {isPending ? (
              <>
                <span className="w-5 h-5 border-2 border-[var(--base)] border-t-transparent rounded-full animate-spin" />
                Sending...
              </>
            ) : (
              'Send Magic Link'
            )}
          </button>
        </form>
      )}
    </div>
  )
}
